import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { NoteBcbaService } from 'src/app/core/services/notes-bcba.service';
import { NoteRbtService } from 'src/app/core/services/notes-rbt.service';
import Swal from 'sweetalert2';
import { getPos, posCode99, textValidatePosCode99 } from '../../utils/getPos';

export interface AISummaryData {
  diagnosis: string;
  birthDate: string | null;
  startTime: string | null;
  endTime: string | null;
  startTime2: string | null;
  endTime2: string | null;
  mood?: string;
  pos: string;
  posDescription?: string | null;
  replacements?: Array<{
    name: string;
    isRequired?: boolean;
    is_required?: boolean;
    firstTrial?: boolean;
    totalTrials?: number;
    correctResponses?: number;
    totalSteps?: number;
    accomplishedSteps?: number;
  }>;
  rbtTrainingGoals?: Array<{goal: string; percentCorrect: number}>;
  participants?: string;
  noteDescription?: string;
  // BCBA specific fields
  cptCode?: string;
  environmentalChanges?: string;
  clientAppeared?: string;
  evidencedBy?: string;
  rbtModeledAndDemonstrated?: string;
  clientResponse?: string;
  progressNoted?: string;
  nextSession?: string;
  assessmentBackground?: string;
  reportIteration?: number;
  // 97151 specific fields
  cpt51type?: 'Observation/Parent Interview' | 'report';
  procedure?: string;
  instruments?: string;
  intakeAndOutcomeMeasurements?: string;
  // 97153 specific fields
  antecedents?: string[];
  interventions?: string[];
  // 97155 specific fields
  maladaptives?: Array<{behavior: string; frequency: number}>;
  replacementsNoteBcba?: Array<{name: string; percentage: number}>;
  wasTheRbtPresent?: boolean;

  modifiedInterventionProtocols?: string;
  assessedInterventionProtocols?: string;

  modifiedReplacementProtocols?: string;
  assessedReplacementProtocols?: string;

  modificationsNeededAtThisTime?: boolean;
  additionalGoalsOrInterventions?: string;
  otherProtocol?:boolean;
  detailsOtherProtocol?:string;
  whyWasTheProtocolModifiedOrNotModified?:string;
  describeInDetailTheProtocolModification?:string;
  // 97156 specific fields
  demonstratedReplacementProtocols?: string;
  demonstratedInterventionProtocols?: string;
  discussedBehaviors?: string;
  caregiverGoals?: string;
  pos_allowed_to_cg?: string[];
  teacherGoals?: string;
  // 97155 and 97156
  modifiedBehaviorProtocols?: string;
  assessedBehaviorProtocols?: string;
}

@Component({
  selector: 'app-generate-ai-summary',
  template: `
    <button
      class="btn btn-primary btn-sm"
      (click)="onGenerateClick()"
      [disabled]="isGenerating">
      {{ isGenerating ? "Generating..." : "Generate AI Summary" }}
    </button>
  `
})
export class GenerateAiSummaryComponent {
  @Input() type: 'rbt' | 'bcba' = 'rbt';
  @Output() summaryGenerated = new EventEmitter<string>();
  @Output() requestData = new EventEmitter<void>();

  isGenerating = false;

   private noteRbtService = inject(NoteRbtService);
  private noteBcbaService = inject(NoteBcbaService);

  onGenerateClick() {
    this.requestData.emit();
  }

  generateSummary(summaryData: AISummaryData) {
    const validationResult = this.validateData(summaryData);
    if (!validationResult.isValid) {
      const Fieldslist = validationResult.missingFields.map(field => `<li>${field}</li>`).join('');

      Swal.fire({
        icon: 'warning',
        title: 'Missing Required Fields',
        html: `
          <p>Please complete the required fields below before generating the AI summary:</p>
          <ul style="text-align: left;">${Fieldslist}</ul>
        `,
        confirmButtonText: 'OK'
      });
      return;
    }

    this.isGenerating = true;
    const service = this.type === 'rbt' ? this.noteRbtService : this.noteBcbaService;

    // Prepare data based on type and CPT code
    const preparedData = this.type === 'bcba' ? this.prepareBcbaData(summaryData) : summaryData;

    const payload: Record<string, unknown> = {
      ...(preparedData as unknown as Record<string, unknown>),
      pos: summaryData.pos===posCode99 ? summaryData.posDescription : getPos(summaryData.pos),
    };
    delete payload['startTime'];
    delete payload['endTime'];
    delete payload['startTime2'];
    delete payload['endTime2'];
    
    service.generateAISummary(payload).subscribe(
      {
        next: (response) => {
          this.summaryGenerated.emit(response.summary);
          this.isGenerating = false;
        },
        error: (error) => {
          console.error('Error generating AI summary:', error);
          const errorMessage = error.error.message || 'Error generating AI summary. Please try again.';
          Swal.fire(
            'Error',
            errorMessage,
            'error'
          );
          this.isGenerating = false;
        }
      }
    );
  }

  private validateData(summaryData: AISummaryData): { isValid: boolean; missingFields: string[] } {
    const missingFields: string[] = [];

    if (!summaryData.cptCode) {
      missingFields.push('CPT Code');
      return {
        isValid: false,
        missingFields
      }
    }

    // Common validations for all types
    if (!summaryData.diagnosis) missingFields.push('Diagnosis');
    if (!summaryData.pos) missingFields.push('POS');
    if (summaryData.pos === '99' && !summaryData.posDescription) missingFields.push(textValidatePosCode99);

    const hasTime1 = summaryData.startTime && summaryData.endTime;
    const hasTime2 = summaryData.startTime2 && summaryData.endTime2;
    if (summaryData.cptCode !== '97153') {
      if (!hasTime1 && !hasTime2) missingFields.push('Session Time');
    }

    if (this.type === 'bcba') {
      switch (summaryData.cptCode) {
        case '97151': {
          const cpt51 = summaryData.cpt51type ? String(summaryData.cpt51type).toLowerCase() : '';

          if (!cpt51) {
            missingFields.push('Assessment Type (Observation/Parent Interview/Report)');
          }

          if (cpt51.includes('observation') || cpt51.includes('observation/parent interview')) {
            if (!summaryData.procedure?.length) {
              missingFields.push('Assessment Procedures');
            }
            if (!summaryData.instruments?.length) {
              missingFields.push('Assessment Tools');
            }
            if (!summaryData.intakeAndOutcomeMeasurements?.length) {
              missingFields.push('Intake and Outcome Measurements');
            }
          }
          break;
        }
        case '97155':
          // Protocol modification validations
          if (!summaryData.participants) missingFields.push('Participants');
          if (!summaryData.environmentalChanges) missingFields.push('Environmental Changes');
          if ((summaryData.maladaptives?.filter(m => m.frequency === null).length ?? 0)
            === (summaryData.maladaptives?.length ?? 0)) {
              missingFields.push('You must enter at least one value in the Maladaptives section');
          }
          if ((summaryData.replacementsNoteBcba?.filter(r => r.percentage === null).length ?? 0)
            === (summaryData.replacementsNoteBcba?.length ?? 0)) {
              missingFields.push('You must enter at least one value in the Replacements section');
          }
          if(!this.checkModifiers(summaryData)){
            missingFields.push('At least one protocol modification must be made');
          }
          if(summaryData.otherProtocol && summaryData.detailsOtherProtocol===''){
            missingFields.push("You must provide details for the modified protocol in the 'Other Protocol Details' section");
          }
          if(!summaryData.whyWasTheProtocolModifiedOrNotModified || summaryData.whyWasTheProtocolModifiedOrNotModified===''){
            missingFields.push('Why was the protocol modified or not modified');
          }
          if(!summaryData.describeInDetailTheProtocolModification || summaryData.describeInDetailTheProtocolModification===''){
            missingFields.push('Describe in Detail the protocol modification');
          }
          if (!summaryData.progressNoted) missingFields.push('Progress noted this session compared to previous session');
          break;

        case '97156':
          // Caregiver training validations
          if(
            summaryData.pos
            && summaryData?.pos_allowed_to_cg
            && summaryData?.pos_allowed_to_cg.length>0
            && summaryData?.pos_allowed_to_cg.includes(summaryData.pos)
          ){
            if (!summaryData.caregiverGoals?.length) {
              missingFields.push('You must enter at least one value in the Caregiver Goals section');
            }
          }

          if (!summaryData.demonstratedReplacementProtocols?.length) {
            missingFields.push('Demonstrated Replacement Protocols');
          }
          if (!summaryData.demonstratedInterventionProtocols?.length) {
            missingFields.push('Demonstrated Intervention Protocols');
          }
          if (!summaryData.discussedBehaviors?.length) {
            missingFields.push('Discussed Behaviors');
          }
          if (!summaryData.participants) missingFields.push('Participants');
          if (!summaryData.environmentalChanges) missingFields.push('Environmental Changes');
          if (!summaryData.progressNoted) missingFields.push('Progress noted this session compared to previous session');
          break;

        case '97153':
          // Direct treatment validations
          missingFields.push('THIS CPT HAS NOT BEEN IMPLEMENTED YET');
          break;
      }
    } else { // RBT validations
      if (!summaryData.maladaptives?.length) {
        missingFields.push('Maladaptive Behaviors');
      } else {
        const invalidMaladaptives = summaryData.maladaptives.some(
          m => !m.behavior || m.frequency === undefined || m.frequency === null
        );
        if (invalidMaladaptives) missingFields.push('Complete Maladaptive Behavior Data');
      }

      if (!summaryData.replacements?.length) {
        missingFields.push('Replacement Behaviors');
      }
      else {
        const invalidReplacements = summaryData.replacements.some((replacement) => {
          const isRequired = this.isReplacementRequired(replacement);

          if (!isRequired) {
            return false;
          }

          if (!replacement.name) {
            return true;
          }

          if (replacement.firstTrial === true) {
            const totalSteps = replacement.totalSteps;
            const accomplishedSteps = replacement.accomplishedSteps;
            const hasValidNumbers =
              typeof totalSteps === 'number' &&
              typeof accomplishedSteps === 'number';

            if (!hasValidNumbers) {
              return true;
            }

            return totalSteps <= 0
              || accomplishedSteps < 0
              || accomplishedSteps > totalSteps;
          }

          return (
            typeof replacement.totalTrials !== 'number' ||
            typeof replacement.correctResponses !== 'number'
          );
        });
        if (invalidReplacements) missingFields.push('Complete Replacement Behavior Data');
      }

      if (!summaryData.interventions?.length) {
        missingFields.push('Interventions');
      }

      if (!summaryData.antecedents?.length) {
        missingFields.push('Antecedents');
      }

      if (!summaryData.mood?.length) {
        missingFields.push('Mood');
      }
      if (!summaryData.evidencedBy?.length) {
        missingFields.push('Evidenced By');
      }
      if (!summaryData.progressNoted?.length) {
        missingFields.push('Progress Noted');
      }
      if (!summaryData.rbtModeledAndDemonstrated?.length) {
        missingFields.push('RBT Modeled and Demonstrated to Caregiver');
      }

    }

    return {
      isValid: missingFields.length === 0,
      missingFields
    };
  }

  private checkModifiers(summaryData: AISummaryData){
    if(
      summaryData.modifiedBehaviorProtocols===''
      && summaryData.modifiedInterventionProtocols===''
      && summaryData.modifiedReplacementProtocols===''
      && !summaryData.otherProtocol
    ){
      return false;
    }
    else{
      return true;
    }
  }

  private isReplacementRequired(replacement: {
    isRequired?: boolean;
    is_required?: boolean;
  }): boolean {
    return replacement.isRequired ?? replacement.is_required ?? false;
  }

  private prepareBcbaData(summaryData: AISummaryData): AISummaryData {
    // Common fields
    const preparedData: AISummaryData = {
      diagnosis: summaryData.diagnosis,
      birthDate: summaryData.birthDate,
      startTime: null,
      endTime: null,
      startTime2: null,
      endTime2: null,
      pos: summaryData.pos,
      cptCode: summaryData.cptCode
    };

    // Only include session times if NOT 97153
    if (summaryData.cptCode !== '97153') {
      preparedData.startTime = summaryData.startTime;
      preparedData.endTime = summaryData.endTime;
      preparedData.startTime2 = summaryData.startTime2;
      preparedData.endTime2 = summaryData.endTime2;
    }

    // CPT-specific fields
    switch (summaryData.cptCode) {
      case '97151': {
        const chosenBackground = summaryData.assessmentBackground || '';
        if (summaryData.cpt51type === 'Observation/Parent Interview') {
          Object.assign(preparedData, {
            cpt51type: 'Observation/Parent Interview',
            procedure: summaryData.procedure,
            instruments: summaryData.instruments,
            intakeAndOutcomeMeasurements: summaryData.intakeAndOutcomeMeasurements,
            assessmentBackground: chosenBackground,
          });
        } else {
          Object.assign(preparedData, {
            cpt51type: 'report',
            procedure: summaryData.procedure,
            assessmentBackground: chosenBackground,
          });
        }
        break;
      }

      case '97155':
        Object.assign(preparedData, {
          maladaptives: summaryData.maladaptives,
          replacements: summaryData.replacementsNoteBcba,
          environmentalChanges: summaryData.environmentalChanges,
          participants: summaryData.participants,
          modifiedBehaviorProtocols: summaryData.modifiedBehaviorProtocols, //agregar en generacion de AI
          modifiedInterventionProtocols: summaryData.modifiedInterventionProtocols,
          modifiedReplacementProtocols: summaryData.modifiedReplacementProtocols,
          assessedBehaviorProtocols: summaryData.assessedBehaviorProtocols,
          assessedInterventionProtocols: summaryData.assessedInterventionProtocols,
          assessedReplacementProtocols: summaryData.assessedReplacementProtocols,
          otherProtocol: summaryData.otherProtocol, //agregar en generacion de AI
          detailsOtherProtocol: summaryData.detailsOtherProtocol, //agregar en generacion de AI
          modificationsNeededAtThisTime: summaryData.modificationsNeededAtThisTime,
          additionalGoalsOrInterventions: summaryData.additionalGoalsOrInterventions,
          progressNoted: summaryData.progressNoted,
          whyWasTheProtocolModifiedOrNotModified: summaryData.whyWasTheProtocolModifiedOrNotModified,
          describeInDetailTheProtocolModifications: summaryData.describeInDetailTheProtocolModification
        });
        break;

      case '97156':
        Object.assign(preparedData, {
          environmentalChanges: summaryData.environmentalChanges,
          participants: summaryData.participants,
          caregiverGoals: summaryData.caregiverGoals,
          teacherGoals: summaryData.teacherGoals,
          demonstratedReplacementProtocols: summaryData.demonstratedReplacementProtocols,
          demonstratedInterventionProtocols: summaryData.demonstratedInterventionProtocols,
          discussedBehaviors: summaryData.discussedBehaviors,
          progressNoted: summaryData.progressNoted
        });
        break;

      case '97153':
        Object.assign(preparedData, {
          environmentalChanges: summaryData.environmentalChanges,
          participants: summaryData.participants,
          clientAppeared: summaryData.clientAppeared,
          evidencedBy: summaryData.evidencedBy,
          clientResponse: summaryData.clientResponse,
          progressNoted: summaryData.progressNoted,
          nextSession: summaryData.nextSession
        });
        break;
    }

    return preparedData;
  }

}
